import { useEffect, useMemo, useRef, useState } from 'react';
import { Input, Select } from '@/components/common';
import { useCityLookup, useCountryLookup, useStateLookup } from '@/hooks/useGeography';
import { usePincodeLookup } from '@/hooks/usePincodeLookup';
import type { StateLookup } from '@/services/geography';

const PIN_CODE_REGEX = /^\d{6}$/;

/**
 * This platform is India-only (IRDAI, GoDigit, ...), but the seeded/live
 * State table also carries unrelated-country QA rows (see reviewer finding:
 * prod's "active, non-deleted" state list included two Brazil test rows
 * alongside the 34 real Indian states/UTs). Every state dropdown built on
 * this component filters down to India's states specifically, rather than
 * trusting `is_active` alone to mean "a real option".
 *
 * Filtering happens here, client-side, against the existing
 * `/geography/countries/lookup/` + `/geography/states/lookup/` responses --
 * NOT by changing the backend queryset, which is out of this lane's file
 * ownership (see action_items_outside_files for the real fix: the states
 * `lookup` action's `is_deleted=False` filter also drops states that active,
 * non-deleted Pincodes still resolve to, e.g. Andaman & Nicobar).
 */
const INDIA_COUNTRY_CODE = 'IND';

/**
 * All of this component's State options, narrowed to India. Fails OPEN
 * (falls back to the unfiltered list) whenever the country lookup hasn't
 * resolved yet, errored, or came back without an India row -- an empty
 * dropdown would violate "never block the form" far worse than a few extra
 * junk options would.
 */
function useIndiaStates() {
  const { data: countries } = useCountryLookup();
  const { data: states, isLoading, isError } = useStateLookup();

  const indiaCountryId = useMemo(() => {
    const list = Array.isArray(countries) ? countries : [];
    return list.find((c) => c.code === INDIA_COUNTRY_CODE)?.id;
  }, [countries]);

  const data = useMemo(() => {
    const list = Array.isArray(states) ? states : [];
    if (!indiaCountryId) return list;
    const indiaOnly = list.filter((s) => s.country === indiaCountryId);
    return indiaOnly.length > 0 ? indiaOnly : list;
  }, [states, indiaCountryId]);

  return { data, isLoading, isError };
}

/**
 * What a state dropdown's `value`/`onChange` actually carry. Every address
 * form on this platform stores the state's NAME as a plain string, except
 * ProposalFormPage, which already stored the two-letter CODE before this
 * component existed. Picking the wrong one here would silently change what
 * gets submitted, so callers must say which shape their backend expects
 * rather than this component guessing.
 *
 * M-04 adds 'id': the Add Policy RTO block scopes the City and RTO lookups by
 * `?state=<uuid>`, so it is the only caller that needs the primary key rather
 * than something a human reads.
 */
export type StateValueKey = 'name' | 'code' | 'id';

function stateValueOf(state: StateLookup, key: StateValueKey): string {
  if (key === 'code') return state.code;
  if (key === 'id') return state.id;
  return state.name;
}

export interface StateSelectProps {
  value: string;
  onChange: (value: string) => void;
  valueKey?: StateValueKey;
  label?: string;
  id?: string;
  name?: string;
  required?: boolean;
  error?: string;
  disabled?: boolean;
  className?: string;
  /**
   * What to call a `value` that matches no row in the states lookup, which is
   * kept as an extra option rather than dropped (see below). Defaults to the
   * value itself, which reads fine for a name or a code -- and not at all for
   * an `id`-valued caller, where the raw UUID would be shown to a broker.
   */
  fallbackLabel?: string;
}

/**
 * B6 -- State is ALWAYS a dropdown of every active state/UT from
 * GET /geography/states/lookup/, never a free-text box, on every address
 * form this batch touches.
 *
 * A value that matches none of the active states -- a state deactivated
 * since, or legacy free text that doesn't line up exactly with a seeded
 * name -- is never dropped on the floor: it is kept as an extra option so
 * the field still shows the truth on file instead of quietly reverting to
 * the placeholder, which would read as "no state" for a record that has
 * one (see feedback_failed_request_is_not_empty).
 */
export function StateSelect({
  value,
  onChange,
  valueKey = 'name',
  label = 'State',
  id = 'state',
  name = 'state',
  required,
  error,
  disabled,
  className,
  fallbackLabel,
}: StateSelectProps) {
  const { data: states, isLoading, isError } = useIndiaStates();

  const options = useMemo(() => {
    const seen = new Set<string>();
    const opts: { value: string; label: string }[] = [];
    // Defensive: a state list that comes back shaped wrong (e.g. paginated
    // instead of a bare array) must degrade to "no options yet", never crash
    // the whole form.
    const list = Array.isArray(states) ? states : [];
    list.forEach((s) => {
      const optionValue = stateValueOf(s, valueKey);
      if (!optionValue || seen.has(optionValue)) return;
      seen.add(optionValue);
      opts.push({ value: optionValue, label: s.name });
    });
    if (value && !seen.has(value)) {
      opts.unshift({ value, label: fallbackLabel || value });
    }
    return opts;
  }, [states, valueKey, value, fallbackLabel]);

  return (
    <Select
      id={id}
      name={name}
      label={label}
      required={required}
      value={value}
      error={
        error || (isError ? 'The states list failed to load — the value on file is kept.' : undefined)
      }
      options={options}
      placeholder={isLoading ? 'Loading states…' : 'Select state'}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className={className}
    />
  );
}

export interface AddressFieldsProps {
  pincode: string;
  onPincodeChange: (value: string) => void;
  pincodeError?: string;
  pincodeRequired?: boolean;
  pincodeLabel?: string;
  pincodeId?: string;
  pincodeName?: string;

  state: string;
  onStateChange: (value: string) => void;
  stateError?: string;
  stateRequired?: boolean;
  stateLabel?: string;
  stateId?: string;
  stateName?: string;
  /** @default 'name' */
  stateValueKey?: StateValueKey;

  /** Omit entirely (along with `onCityChange`) on a form with no City box. */
  city?: string;
  onCityChange?: (value: string) => void;
  cityError?: string;
  cityLabel?: string;
  cityId?: string;
  cityName?: string;

  disabled?: boolean;
}

/**
 * B6 -- Pincode + (optional) City + State, wired so a 6-digit PIN pre-fills
 * State (and City, when the lookup returns one and the caller renders a City
 * box) while State stays a manually-changeable dropdown.
 *
 * B9 adds the FORWARD direction on top of B6's reverse one: City is now a
 * dropdown SCOPED TO the selected State (`GET /geography/cities/lookup/
 * ?state=`, already used elsewhere via `useCityLookup` — no backend change
 * needed). PIN stays free text (a state has far more valid PIN codes than the
 * `/geography/pincodes/lookup/` dropdown endpoint returns — it caps at 100
 * per `PincodeViewSet.lookup`, nowhere near enough to enumerate a state like
 * Maharashtra — so a real `<select>` there would silently hide legitimate
 * codes; see `handlePincodeBlur` below for the state-level VALIDATION that
 * replaces it). City, by contrast, has no such cap (`CityViewSet.lookup` is
 * unbounded), so a real dropdown is honest there.
 *
 * Deliberately a Fragment of three independent form controls, not a
 * wrapping <div>: every page that uses this drops it straight into an
 * existing CSS grid (`grid-cols-…`) as sibling cells, exactly where the
 * three fields it replaces used to sit.
 *
 * Auto-fill only ever runs from a PIN code BLUR, the same trigger the health
 * quote wizard already used before this component existed, and only once
 * per distinct 6-digit code (see usePincodeLookup's cache) — so:
 *  - a user who manually overrides an auto-filled State keeps that choice
 *    on every subsequent blur of the SAME code; it is only replaced when the
 *    PIN code itself changes to a new one that resolves to a different
 *    state, which is the one case the spec calls out as fine to overwrite.
 *  - loading an existing record's saved pincode+state, and then TABBING
 *    THROUGH the pincode field without typing into it, never fires a
 *    lookup either — including when that saved value only arrives a tick
 *    after mount (react-hook-form's `reset()` once a fetch resolves, the
 *    real shape every edit form in this batch uses). `lastResolvedCode` is
 *    resynced to `pincode` whenever it changes to something other than the
 *    value THIS component itself last asked the parent to set (compared via
 *    `selfInitiatedValue`) — so a value received only as a prop (a saved
 *    record loading, hydrating late, or being swapped out from under this
 *    instance) is never mistaken for "the user just typed a new PIN" the
 *    next time the field is blurred, no matter whether that happens before
 *    or after the field is first focused.
 * A failed or empty lookup never blocks the form or clears an existing
 * State — it leaves the dropdown exactly as it was and shows a quiet hint.
 *
 * B9's own rule on top of that: once a State is ALREADY selected AND matches
 * a real row from the states lookup, a PIN blur that resolves to a DIFFERENT
 * state is no longer auto-filled over it -- a real, intentional State choice
 * is not silently overwritten by a typed PIN that disagrees with it. Instead
 * the PIN field shows a validation error naming the mismatch, and State/City
 * are left exactly as they were. This intentionally supersedes B6's old
 * "a changed PIN always wins" rule for that one case (see the removed test
 * `overwrites State when the PIN changes to a different one` in
 * `AddressFields.test.tsx`, replaced by a mismatch-is-flagged test) -- silently
 * discarding a real State selection is exactly the kind of data loss the rest
 * of this component (`StateSelect`'s "never dropped on the floor") already
 * refuses to do elsewhere; B6 simply had not been asked to protect State from
 * a DISAGREEING pin yet.
 *
 * The comparison is against a RESOLVED state only, never raw legacy text: an
 * unmatched value (e.g. "New Delhi" on file where the seeded name is
 * "Delhi") is not "real" enough to protect, and the pre-B9 auto-fill
 * behaviour applies to it unchanged -- with no State chosen yet, or an
 * unresolved one, a found PIN still fills State (and City) exactly as
 * before.
 */
export function AddressFields({
  pincode,
  onPincodeChange,
  pincodeError,
  pincodeRequired,
  pincodeLabel = 'Pincode',
  pincodeId = 'pincode',
  pincodeName = 'pincode',

  state,
  onStateChange,
  stateError,
  stateRequired,
  stateLabel = 'State',
  stateId = 'state',
  stateName = 'state',
  stateValueKey = 'name',

  city,
  onCityChange,
  cityError,
  cityLabel = 'City',
  cityId = 'city',
  cityName = 'city',

  disabled,
}: AddressFieldsProps) {
  const { data: states } = useIndiaStates();
  const { lookup, loading } = usePincodeLookup();
  const [hint, setHint] = useState<string | null>(null);
  // B9: set on a PIN blur that disagrees with a State the USER THEMSELVES
  // picked from the dropdown (see `handleManualStateChange` and
  // `handlePincodeBlur` below). A real validation error (rendered through
  // `error`, not `helperText`) -- distinct from `hint`, which only ever
  // reports "could not look this PIN up at all".
  const [stateMismatchError, setStateMismatchError] = useState<string | null>(null);
  // What code (if any) is already reflected in State/City, so a repeat blur
  // of the same, unchanged code never re-fires the lookup.
  const lastResolvedCode = useRef<string | null>(null);
  // B9: the value WE last asked the parent to set via the State dropdown's
  // OWN onChange (`handleManualStateChange`) -- i.e. the user directly
  // choosing a state, as opposed to `state` changing because a record loaded,
  // or because THIS component's own PIN auto-fill (below) set it. Compared
  // against the next `state` prop exactly like `selfInitiatedValue` already
  // does for `pincode`: a mismatch means `state` changed some other way, so
  // it is not something the user just deliberately picked.
  const stateSelfInitiatedValue = useRef<string | null>(null);
  // Whether the CURRENT `state` value is standing because the user picked it
  // from the dropdown (true) or arrived some other way -- hydration, or this
  // component's own PIN-driven auto-fill (false either way). Only a value the
  // user put there themselves is treated as deliberate enough to protect from
  // a disagreeing PIN; B6's existing "a changed PIN wins" rule is otherwise
  // left completely alone (a hydrated record's State, and a PREVIOUS PIN's
  // own auto-filled State, both keep updating freely -- see
  // `AddressFields.test.tsx`, `ProfilePagePinState.test.tsx` and
  // `BrokersListPagePinState.test.tsx`, all of which rely on exactly that).
  const stateWasManuallyPicked = useRef(false);

  // True between a PIN auto-fill that set both State and City and the State id
  // change it causes, so that one change does not clear the City just filled.
  const cityFilledByPin = useRef(false);

  const handleManualStateChange = (value: string) => {
    stateSelfInitiatedValue.current = value;
    stateWasManuallyPicked.current = true;
    cityFilledByPin.current = false;
    onStateChange(value);
  };

  useEffect(() => {
    if (state !== stateSelfInitiatedValue.current) {
      stateWasManuallyPicked.current = false;
    }
  }, [state]);
  // The value WE last asked the parent to set via `onPincodeChange` (i.e.
  // the user actually typing into this field). Compared against the next
  // `pincode` prop we receive: a match means this render is just that edit
  // completing its round trip; a MISMATCH means `pincode` changed some
  // other way -- an edit form's data hydrating a tick after mount
  // (react-hook-form's `reset()`, see CustomerFormPage et al.) is the real
  // one, but this also covers a plain saved-record mount. Either way, a
  // value this component never asked for is never mistaken for "the user
  // just typed a new PIN" on the next blur.
  const selfInitiatedValue = useRef<string | null>(null);

  useEffect(() => {
    const trimmed = pincode ? pincode.trim() : null;
    if (trimmed !== selfInitiatedValue.current) {
      lastResolvedCode.current = trimmed;
    }
  }, [pincode]);

  const resolveStateValue = (resolvedStateName: string): string => {
    if (stateValueKey === 'name') return resolvedStateName;
    const match = (Array.isArray(states) ? states : []).find(
      (s) => s.name.toLowerCase() === resolvedStateName.toLowerCase()
    );
    return match ? match.code : resolvedStateName;
  };

  // B9: the currently-selected State row, found by whichever value shape
  // this caller stores (`stateValueKey`) -- needed for its real NAME (to
  // compare against a PIN's resolved state, and to build the "belongs to X"
  // message) and its id (to scope the City dropdown by `?state=`).
  const selectedState = useMemo(
    () =>
      state
        ? (Array.isArray(states) ? states : []).find(
            (s) => stateValueOf(s, stateValueKey).toLowerCase() === state.toLowerCase()
          )
        : undefined,
    [states, state, stateValueKey]
  );
  const selectedStateId = selectedState?.id;
  const selectedStateName = selectedState?.name;

  const handlePincodeBlur = async (raw: string) => {
    const code = raw.trim();
    if (!PIN_CODE_REGEX.test(code) || code === lastResolvedCode.current) {
      return;
    }
    lastResolvedCode.current = code;
    const outcome = await lookup(code);
    if (outcome.status === 'found') {
      // B9: only a State the user THEMSELVES picked from the dropdown is
      // protected from a disagreeing PIN -- `stateWasManuallyPicked` is false
      // for a hydrated record's State and for a state this same component
      // previously auto-filled, both of which keep updating from a new PIN
      // exactly as B6 always did (see the ref's own comment for the tests
      // this distinction exists to keep green).
      const mismatchesSelectedState =
        stateWasManuallyPicked.current &&
        !!selectedStateName &&
        selectedStateName.trim().toLowerCase() !== outcome.stateName.trim().toLowerCase();
      if (mismatchesSelectedState) {
        // A State the user deliberately chose disagrees with this PIN -- do
        // NOT silently overwrite that choice. Flag it instead and leave
        // State/City exactly as they were; the person decides which one to
        // fix.
        setStateMismatchError(
          `This PIN code belongs to ${outcome.stateName}, not ${selectedStateName} — please check the PIN code or the selected State.`
        );
      } else {
        setStateMismatchError(null);
        onStateChange(resolveStateValue(outcome.stateName));
        // This auto-fill is not a manual pick -- a LATER PIN may still freely
        // update it, same as before B9.
        stateWasManuallyPicked.current = false;
        if (onCityChange && outcome.cityName) {
          onCityChange(outcome.cityName);
          // The State is about to change to the PIN's own, so the effect below
          // that clears City on a State change must not wipe this one.
          cityFilledByPin.current =
            !selectedStateName || selectedStateName.trim().toLowerCase() !== outcome.stateName.trim().toLowerCase();
        }
      }
      setHint(null);
    } else {
      setStateMismatchError(null);
      setHint('Could not auto-fill State for this PIN code — please choose it manually.');
    }
  };

  // A State change makes any earlier mismatch stale -- it was a complaint
  // about the OLD selection, which no longer applies either way.
  useEffect(() => {
    setStateMismatchError(null);
  }, [state]);

  // B9: when the user DELIBERATELY changes State, the City on file belongs to
  // the previous state and must go.
  //
  // Without this the city survived, and `cityOptions` below then re-inserted it
  // at the top of the new state's list under the "never drop a value on file"
  // rule -- so switching Maharashtra to Kerala left "Mumbai" selected and
  // offered as though it were a Kerala city, and the form saved that pair.
  //
  // The distinction that matters is USER CHANGE vs HYDRATION. A form loading an
  // existing record resolves its state id from undefined to a value once; that
  // is not a change and must not wipe the saved city (several forms depend on
  // it, and the PIN-first path in #273 sets state only after the city). So the
  // reset fires only when a previously RESOLVED state id becomes a different
  // resolved id.
  const previousStateIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const previous = previousStateIdRef.current;
    previousStateIdRef.current = selectedStateId;
    if (previous && selectedStateId && previous !== selectedStateId) {
      if (cityFilledByPin.current) {
        cityFilledByPin.current = false;
        return;
      }
      onCityChange?.('');
    }
  }, [selectedStateId, onCityChange]);

  // B9: City, scoped to the selected State. `enabled: false` while there is
  // no resolved state id keeps this from ever requesting (or showing) an
  // unscoped, nationwide city list -- unlike State's own "fail open to the
  // unfiltered list" rule, there is no safe unscoped fallback here: a few
  // dozen extra states is tolerable clutter, tens of thousands of cities
  // nationwide is not a dropdown.
  const {
    data: citiesForState,
    isLoading: citiesLoading,
    isError: citiesIsError,
  } = useCityLookup(selectedStateId, { enabled: !!selectedStateId });

  const cityOptions = useMemo(() => {
    const seen = new Set<string>();
    const opts: { value: string; label: string }[] = [];
    const list = selectedStateId && Array.isArray(citiesForState) ? citiesForState : [];
    list.forEach((c) => {
      if (!c.name || seen.has(c.name)) return;
      seen.add(c.name);
      opts.push({ value: c.name, label: c.name });
    });
    // Same "never drop a value on file" rule as StateSelect: a city already
    // saved (from before this state was selected, a differently-spelled
    // legacy value, or simply not yet loaded) stays visible and selected
    // rather than silently reverting to blank.
    if (city && !seen.has(city)) {
      opts.unshift({ value: city, label: city });
    }
    return opts;
  }, [citiesForState, selectedStateId, city]);

  // Only reached once `selectedStateId` is set (see the render below, which
  // falls back to a plain text Input until then), so there is no "select a
  // state first" branch to cover here.
  const cityPlaceholder = citiesLoading
    ? 'Loading cities…'
    : cityOptions.length === 0
      ? 'No cities found for this state'
      : 'Select city';

  return (
    <>
      <Input
        id={pincodeId}
        name={pincodeName}
        label={pincodeLabel}
        inputMode="numeric"
        maxLength={6}
        required={pincodeRequired}
        value={pincode}
        // B9: the caller's own validation error always wins over this
        // component's own state-mismatch check -- a required-field or
        // format error is a harder stop than "this looks like the wrong
        // state's PIN".
        error={pincodeError || stateMismatchError || undefined}
        helperText={loading ? 'Looking up State…' : (hint ?? undefined)}
        onChange={(e) => {
          const next = e.target.value.replace(/\D/g, '').slice(0, 6);
          selfInitiatedValue.current = next || null;
          onPincodeChange(next);
          if (hint) setHint(null);
          if (stateMismatchError) setStateMismatchError(null);
        }}
        onBlur={(e) => {
          void handlePincodeBlur(e.target.value);
        }}
        disabled={disabled}
      />
      {onCityChange &&
        (selectedStateId ? (
          <Select
            id={cityId}
            name={cityName}
            label={cityLabel}
            value={city ?? ''}
            error={
              cityError || (citiesIsError ? 'The cities list failed to load — the value on file is kept.' : undefined)
            }
            options={cityOptions}
            placeholder={cityPlaceholder}
            onChange={(e) => onCityChange(e.target.value)}
            disabled={disabled}
          />
        ) : (
          // B9: with no State resolved yet, City has nothing to be scoped BY
          // -- an unscoped, nationwide dropdown is not a safe fallback (see
          // the module doc), so this falls back to free text exactly as
          // before B9, rather than a select with no usable options. Filling
          // in State (by hand, or via a PIN auto-fill above) upgrades this to
          // the real scoped dropdown on the next render.
          <Input
            id={cityId}
            name={cityName}
            label={cityLabel}
            value={city ?? ''}
            error={cityError}
            onChange={(e) => onCityChange(e.target.value)}
            disabled={disabled}
          />
        ))}
      <StateSelect
        id={stateId}
        name={stateName}
        label={stateLabel}
        required={stateRequired}
        value={state}
        onChange={handleManualStateChange}
        valueKey={stateValueKey}
        error={stateError}
        disabled={disabled}
      />
    </>
  );
}
